class Slider {
  constructor(container) {
    this.container = container;
    this.mainSlides = this.container.querySelectorAll('.slide-item');
    this.thumbnails = this.container.querySelectorAll('.thumbnail-item');
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.transitionDuration = this.getTransitionDuration();
    this.isVisible = true;
    
    this.init();
  }

  getTransitionDuration() {
    const durationStr = getComputedStyle(document.documentElement)
      .getPropertyValue('--transition-duration').trim() || '0.6s';
    return parseFloat(durationStr) * 1000;
  }

  init() {
    // Add click handlers to thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        if (!this.isTransitioning && index !== this.currentIndex) {
          this.showSlide(index);
        }
      });
    });

    // Show first slide initially
    this.showSlide(0);

    // Add keyboard navigation
    this.handleKeyboardNavigation = this.handleKeyboardNavigation.bind(this);
    document.addEventListener('keydown', this.handleKeyboardNavigation);

    // Add touch support
    this.addTouchSupport();

    // Add resize handler
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
    
    // Initial resize check
    this.handleResize();

    // Handle visibility changes
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;
    });

    // Handle theme editor events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this.container)) {
        this.init();
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(this.container)) {
        this.destroy();
      }
    });

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this.container)) {
        this.handleSectionSelect();
      }
    });
  }

  destroy() {
    document.removeEventListener('keydown', this.handleKeyboardNavigation);
    window.removeEventListener('resize', this.handleResize);
    // Remove any other event listeners
    this.container.removeEventListener('touchstart', this.handleTouchStart);
    this.container.removeEventListener('touchend', this.handleTouchEnd);
  }

  handleKeyboardNavigation(e) {
    // Only handle keyboard events when slider is visible
    if (!this.isVisible) return;

    if (e.key === 'ArrowLeft') {
      this.showSlide(this.currentIndex - 1);
    } else if (e.key === 'ArrowRight') {
      this.showSlide(this.currentIndex + 1);
    }
  }

  addTouchSupport() {
    this.touchStartX = 0;
    this.touchStartY = 0;
    
    this.handleTouchStart = (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    };

    this.handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe(this.touchStartX, touchEndX, this.touchStartY, touchEndY);
    };

    this.container.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.container.addEventListener('touchend', this.handleTouchEnd, { passive: true });
  }

  handleResize() {
    const isMobile = window.innerWidth < 750;
    this.container.classList.toggle('is-mobile', isMobile);

    // Ensure the active thumbnail is visible
    if (isMobile) {
      const activeThumb = this.thumbnails[this.currentIndex];
      if (activeThumb) {
        this.scrollToThumbnail(activeThumb);
      }
    }
  }

  handleSwipe(startX, endX, startY, endY) {
    const diffX = startX - endX;
    const diffY = startY - endY;

    // Only handle horizontal swipes (ignore more vertical swipes)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        this.showSlide(this.currentIndex + 1);
      } else {
        this.showSlide(this.currentIndex - 1);
      }
    }
  }

  scrollToThumbnail(thumbnail) {
    const thumbnailsGrid = thumbnail.closest('.thumbnails-grid');
    if (!thumbnailsGrid) return;

    const isCenterPosition = thumbnailsGrid.closest('.thumbnails-center');
    const scrollOptions = { behavior: 'smooth' };

    if (isCenterPosition) {
      // For center position, scroll horizontally
      const scrollLeft = thumbnail.offsetLeft - (thumbnailsGrid.offsetWidth / 2) + (thumbnail.offsetWidth / 2);
      thumbnailsGrid.scrollTo({
        ...scrollOptions,
        left: Math.max(0, scrollLeft)
      });
    } else {
      // For left/right position, scroll vertically
      const scrollTop = thumbnail.offsetTop - (thumbnailsGrid.offsetHeight / 2) + (thumbnail.offsetHeight / 2);
      thumbnailsGrid.scrollTo({
        ...scrollOptions,
        top: Math.max(0, scrollTop)
      });
    }
  }

  handleSectionSelect() {
    // Ensure the slider is visible in the theme editor
    this.container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  showSlide(index) {
    if (this.isTransitioning) return;
    
    // Handle circular navigation
    if (index < 0) {
      index = this.mainSlides.length - 1;
    } else if (index >= this.mainSlides.length) {
      index = 0;
    }

    this.isTransitioning = true;

    // Update slides
    const currentSlide = this.mainSlides[this.currentIndex];
    const nextSlide = this.mainSlides[index];

    // Remove previous class from all slides
    this.mainSlides.forEach(slide => {
      slide.classList.remove('previous', 'active');
    });
    
    // Add previous class to current slide
    currentSlide.classList.add('previous');
    
    // Activate next slide
    nextSlide.classList.add('active');

    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
      thumb.setAttribute('aria-selected', i === index);
    });

    // Ensure the active thumbnail is visible
    if (window.innerWidth < 750) {
      this.scrollToThumbnail(this.thumbnails[index]);
    }

    // Reset transition lock after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
      currentSlide.classList.remove('previous');
    }, this.transitionDuration);

    this.currentIndex = index;
  }
}

// Initialize all sliders on the page
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-slider]');
  sliders.forEach(slider => new Slider(slider));
});